import type { PlayerRef } from '@remotion/player';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AssetState, EditorStarterAsset } from './assets/assets';
import { CaptioningTask } from './captioning/caption-state';
import { EditModeProvider } from './edit-mode';
import { SmartGuideProvider } from './smart-guides';
import { EditorStarterItem } from './items/item-type';
import { TransitionItem } from './items/transition/transition-item-type';
import { TextItemHoverPreview } from './items/text/override-text-item-with-hover-preview';
import { ItemBeingTrimmed } from './items/trim-indicator';
import { RenderingTask } from './rendering/render-state';
import { getInitialState } from './state/initial-state';
import { loadLinkingEnabled } from './state/linking-persistance';
import { loadLoop } from './state/loop-persistance';
import { loadSkimmingEnabled } from './state/skimming-persistance';
import { loadSnappingEnabled } from './state/snapping-persistance';
import { loadTimelineCollapsed } from './state/timeline-collapsed-persistance';
import {
  DEFAULT_TIMELINE_HEIGHT,
  loadTimelineHeight,
} from './state/timeline-height-persistance';
import { EditorState, TrackType, UndoableState } from './state/types';
import { DragPreviewProvider } from './timeline/drag-preview-provider';
import { AssetDropPreviewProvider } from './timeline/asset-drop-preview-provider';
import { TransitionDropPreviewProvider } from './timeline/transition-drop-preview-provider';
import { AnimationDropPreviewProvider } from './timeline/animation-drop-preview-provider';
import { SnapPoint } from './timeline/utils/snap-points';
import { getCompositionDuration } from './utils/get-composition-duration';
import { TimelineZoomProvider } from './utils/timeline-zoom-provider';
import { createEditorHistorySnapshot, useUndoRedo } from './utils/undo-redo';
import { TextSelectionProvider } from './contexts/text-selection-context';
import { AssetFramesProvider } from './contexts/asset-frames-context';
import { autoCollapseMagneticGaps } from './state/actions/auto-collapse-magnetic-gaps';
import { sanitizeOrphanedTrackItems } from './state/actions/sanitize-orphaned-track-items';
import { normalizeWakaCaptionGeometry } from '@waka/compat/source-caption-presentation';
import { syncGroupChildrenTiming } from './items/group/sync-group-children-timing';
import {preservesGroupTimingOnPropertyUpdate} from '@waka/compat/group-timing-preservation';
import type {GroupPropertyEdit} from '@waka/compat/group-timing-preservation';

export type SetState = (options: {
  update: EditorState | ((state: EditorState) => EditorState);
  commitToUndoStack: boolean;
  /**
   * true인 경우, 새 undo 포인트를 추가하지 않고 현재 undo 포인트의 상태를 교체합니다.
   * 비동기 작업 완료 후 최종 상태를 반영할 때 유용합니다.
   * commitToUndoStack가 true이고 replaceCurrentUndoEntry도 true면, replace가 우선합니다.
   */
  replaceCurrentUndoEntry?: boolean;
  /** Local gesture ownership, used to detect concurrent edits. Never an authorization token. */
  mutationOwner?: string;
  /** Local property writer intent, validated against the delta and never persisted. */
  groupPropertyEdit?: GroupPropertyEdit;
  /** Compare-and-swap document edit; rejected/no-op updates never create history. */
  expectedUndoableState?: EditorState['undoableState'];
}) => void;

type InternalSetState = (options: Parameters<SetState>[0] & {
  onProcessed?: (before: EditorState, after: EditorState) => void;
  shouldCommit?: (state: EditorState) => boolean;
}) => void;

export type TimelineWriteOnlyContext = {
  /** Imperative read for prepared asynchronous commands; null after this editor has unmounted. */
  getCurrentState?: () => EditorState | null;
  setState: SetState;
  undo: () => void;
  redo: () => void;
};

export interface TimelineContext {
  durationInFrames: number;
}

export interface TracksContext {
  tracks: TrackType[];
}

export interface FpsContext {
  fps: number;
}

export interface DimensionsContext {
  compositionWidth: number;
  compositionHeight: number;
}

export interface SelectedItemsContext {
  selectedItems: string[];
  /** 현재 진입한 그룹 id(없으면 null). 그룹 단위 선택/멤버 depth 구분용. */
  activeGroupId: string | null;
  /** 1급 GroupItem inspector 에서 depth 진입한 멤버 id(표시 전용, 없으면 null). */
  inspectedGroupMemberId: string | null;
}

export interface AssetsContext {
  assets: Record<string, EditorStarterAsset>;
}

export interface AssetStatusContext {
  assetStatus: Record<string, AssetState>;
}

export interface CanUseUndoStackContext {
  canUndo: boolean;
  canRedo: boolean;
}

export interface CurrentStateContext {
  state: React.RefObject<EditorState>;
}

export interface AllItemsContext {
  items: Record<string, EditorStarterItem>;
}

export interface TransitionsContext {
  transitions: Record<string, TransitionItem>;
}

export interface ActiveTimelineSnap {
  activeSnapPoint: SnapPoint | null;
}

export interface BackgroundColorContext {
  backgroundColor: string;
}

export interface BrandTemplateIdContext {
  brandTemplateId: number | null;
}

export interface MaskContextValue {
  mask: UndoableState['mask'];
}

export const TimelineContext = createContext<TimelineContext | null>(null);
export const TimelineWriteOnlyContext =
  createContext<TimelineWriteOnlyContext | null>(null);
export const RenderingContext = createContext<RenderingTask[] | null>(null);
export const FpsContext = createContext<FpsContext | null>(null);
export const DimensionsContext = createContext<DimensionsContext | null>(null);
export const SelectedItemsContext = createContext<SelectedItemsContext | null>(
  null,
);
export const AssetsContext = createContext<AssetsContext | null>(null);
export const AssetStatusContext = createContext<AssetStatusContext | null>(
  null,
);
export const TracksContext = createContext<TracksContext | null>(null);
export const AllItemsContext = createContext<AllItemsContext | null>(null);
export const TransitionsContext = createContext<TransitionsContext | null>(
  null,
);
export const FullStateContext = createContext<EditorState | null>(null);
export const CanUseUndoStackContext =
  createContext<CanUseUndoStackContext | null>(null);
export const CurrentStateContext = createContext<CurrentStateContext | null>(
  null,
);
export const TextItemEditingContext = createContext<string | null>(null);
export const TextItemHoverPreviewContext =
  createContext<TextItemHoverPreview | null>(null);
export const StateInitializedContext = createContext<boolean>(false);
export const CaptionStateContext = createContext<CaptioningTask[]>([]);
export const ItemsBeingTrimmedContext = createContext<ItemBeingTrimmed[]>([]);
export const LoopContext = createContext<boolean>(true);
export const TimelineSnappingEnabledContext = createContext<boolean | null>(
  null,
);
export const SkimmingEnabledContext = createContext<boolean | null>(null);
export const LinkingEnabledContext = createContext<boolean | null>(null);
export const TimelineCollapsedContext = createContext<boolean>(false);
export const TimelineHeightContext = createContext<number>(
  DEFAULT_TIMELINE_HEIGHT,
);
export const ActiveTimelineSnapContext =
  createContext<ActiveTimelineSnap | null>(null);

/// Custom Contexts
export const BackgroundColorContext = createContext<BackgroundColorContext>({
  backgroundColor: 'transparent',
});

export const BrandTemplateIdContext = createContext<BrandTemplateIdContext>({
  brandTemplateId: null,
});

export const MaskContext = createContext<MaskContextValue | null>(null);

export const ItemSelectedForCropContext = createContext<string | null>(null);

// 줌 영역 편집 중인 아이템 — ZoomWrapper 가 편집 중엔 identity 로 렌더해
// 원본 콘텐츠 위에서 영역을 잡을 수 있게 한다. 렌더 서비스에선 기본값 null.
export const ItemSelectedForZoomRegionContext = createContext<string | null>(
  null,
);

export const PlayerRefContext =
  createContext<React.RefObject<PlayerRef | null> | null>(null);

type ContextProviderProps = {
  children: React.ReactNode;
};

export const ContextProvider = ({ children }: ContextProviderProps) => {
  const commandScopeAlive = useRef(true);
  useEffect(() => {
    commandScopeAlive.current = true;
    return () => {
      commandScopeAlive.current = false;
    };
  }, []);
  const [state, setStateWithoutHistory] = useState<EditorState>(() =>
    getInitialState(),
  );

  const imperativeState = useRef(state);
  imperativeState.current = state;

  // 캡션 최초 병합, 분리 했을 때 undo stack 쌓이지 않아 작성한 코드 (버그 발생시 해당 부분 볼 필요있음)
  const hasCommittedInitialSnapshotRef = useRef(false);

  const initialize = useCallback(async () => {
    setStateWithoutHistory((prev) => ({
      ...prev,
      initialized: true,
      loop: loadLoop(),
      timelineHeight: loadTimelineHeight(),
      isSnappingEnabled: loadSnappingEnabled(),
      isSkimmingEnabled: loadSkimmingEnabled(),
      isLinkingEnabled: loadLinkingEnabled(),
      isTimelineCollapsed: loadTimelineCollapsed(),
    }));

    /* 다른 페이지 or 프로젝트 이동 시에 이전 프로젝트 비디오가 보이는 문제 수정 */
  }, []);

  useEffect(() => {
    initialize().catch(console.error);
  }, [initialize]);

  const { undo, redo, pushHistory, replaceCurrentHistory, canUndo, canRedo } =
    useUndoRedo(setStateWithoutHistory);

  const isItemBeingTrimmed =
    state.itemsBeingTrimmed.length > 0 ||
    state.timelineMoveActive === true ||
    Boolean(state.timelineInputOwner);

  const durationInFrames = useMemo(() => {
    return getCompositionDuration(
      state.undoableState.items,
      state.undoableState.tracks,
    );
  }, [state.undoableState.items, state.undoableState.tracks]);

  const lastDurationWhileNotTrimming = useRef(durationInFrames);
  if (!isItemBeingTrimmed) {
    lastDurationWhileNotTrimming.current = durationInFrames;
  }

  const setStateWithPossibleStrictModeDoubleTrigger: InternalSetState = useCallback(
    ({ update, commitToUndoStack, replaceCurrentUndoEntry, mutationOwner, groupPropertyEdit, expectedUndoableState, onProcessed, shouldCommit }) => {
      setStateWithoutHistory((prev) => {
        if (expectedUndoableState && prev.undoableState !== expectedUndoableState) return prev;
        const updatedState =
          typeof update === 'function' ? update(prev) : update;
        if (expectedUndoableState && updatedState === prev) return prev;

        // 트랙에 남아있지만 items 맵에 없는 orphan 참조 정리
        const sanitizedState = sanitizeOrphanedTrackItems(updatedState);

        // Magnetic auto-collapse post-processing
        // 아이템 삭제/축소 시 magnetic 트랙의 간격을 자동으로 제거
        // Exact frame fit or a natural voice swap inside unchanged parent bounds
        // must not ripple siblings. Validate the complete delta before magnetic processing.
        const preservesExplicitInterval = typeof groupPropertyEdit === 'object' &&
          (groupPropertyEdit.type === 'fit-frame' || groupPropertyEdit.type === 'voice-natural' || groupPropertyEdit.type === 'voice-window') &&
          preservesGroupTimingOnPropertyUpdate(prev, sanitizedState, groupPropertyEdit);
        // Let the existing transaction-marker path consume its one-shot flag.
        const collapsedState = preservesExplicitInterval && !sanitizedState.timelineTimeEditApplied
          ? sanitizedState
          : autoCollapseMagneticGaps(prev, sanitizedState);

        // GroupItem 시간 동기화 — 그룹 막대를 옮기거나 트림하면 children 에 전파하고,
        // 멤버가 바뀌면 그룹의 시간 캐시를 union 으로 되맞춘다(시간 SoT = children union).
        // 타임라인 드래그·트림·마그네틱 push 등 모든 write 가 여기를 지나므로 한 곳에서 잡는다.
        const syncedItems = (preservesExplicitInterval || preservesGroupTimingOnPropertyUpdate(prev, collapsedState, groupPropertyEdit))
          ? collapsedState.undoableState.items
          : syncGroupChildrenTiming(
              prev.undoableState.items,
              collapsedState.undoableState.items,
            );
        const groupSyncedState =
          syncedItems === collapsedState.undoableState.items
            ? collapsedState
            : {
                ...collapsedState,
                undoableState: {
                  ...collapsedState.undoableState,
                  items: syncedItems,
                },
              };
        const normalizedState = normalizeWakaCaptionGeometry(
          prev,
          groupSyncedState,
        );
        const newState =
          normalizedState.undoableState === prev.undoableState
            ? normalizedState
            : {
                ...normalizedState,
                documentMutationOwner: mutationOwner ?? null,
              };
        onProcessed?.(prev, newState);
        // undo 히스토리 스냅샷은 동기화가 끝난 최종 newState 기준으로 만든다.
        const historySnapshot = createEditorHistorySnapshot(newState);

        if (!shouldCommit || shouldCommit(newState)) {
          if (replaceCurrentUndoEntry) {
            replaceCurrentHistory(historySnapshot);
          } else if (commitToUndoStack) {
            pushHistory(historySnapshot);
          }
        }

        if (newState === prev) {
          return prev;
        }

        return newState;
      });
    },
    [pushHistory, replaceCurrentHistory],
  );

  const setState: SetState = useCallback(
    ({ update, commitToUndoStack, replaceCurrentUndoEntry, mutationOwner, groupPropertyEdit, expectedUndoableState }) => {
      if (expectedUndoableState) {
        // React may accept either evaluation when replaying an updater. Keep
        // every candidate, then commit only the candidate actually installed.
        const transactionResults = new WeakMap<EditorState['undoableState'], EditorState>();
        setStateWithPossibleStrictModeDoubleTrigger({
          update, expectedUndoableState, groupPropertyEdit, mutationOwner,
          commitToUndoStack: false,
          onProcessed: (before, after) => {
            if (before.undoableState !== after.undoableState) {
              transactionResults.set(after.undoableState, before);
            }
          },
        });
        if (commitToUndoStack || replaceCurrentUndoEntry) {
          setStateWithPossibleStrictModeDoubleTrigger({
            update: state => state, commitToUndoStack, replaceCurrentUndoEntry,
            shouldCommit: state => {
              const transactionBase = transactionResults.get(state.undoableState);
              if (!transactionBase) return false;
              if (commitToUndoStack && !hasCommittedInitialSnapshotRef.current && transactionBase.initialized) {
                pushHistory(createEditorHistorySnapshot(transactionBase));
                hasCommittedInitialSnapshotRef.current = true;
              }
              return true;
            },
          });
        }
        return;
      }
      // 캡션 최초 병합, 분리 했을 때 undo stack 쌓이지 않아 작성한 코드 (버그 발생시 해당 부분 볼 필요있음)
      if (
        commitToUndoStack &&
        !hasCommittedInitialSnapshotRef.current &&
        imperativeState.current.initialized
      ) {
        pushHistory(createEditorHistorySnapshot(imperativeState.current));
        hasCommittedInitialSnapshotRef.current = true;
      }

      // undo stack은 state가 reference로 변경되었는지 확인합니다.
      // state를 변경하고 undo stack에 commit하는 action이 있으면,
      // strict mode double trigger로 인해 state가 두 번 변경될 수 있습니다.

      // 이를 방지하기 위해 먼저 state를 변경합니다 (가능하면 두 번).
      // 그 다음 state를 변경하지 않고 undo stack에 commit합니다.

      setStateWithPossibleStrictModeDoubleTrigger({
        update,
        commitToUndoStack: false,
        mutationOwner,
        groupPropertyEdit,
      });
      if (replaceCurrentUndoEntry) {
        // 현재 undo 포인트를 새 상태로 교체 (비동기 작업 완료 시 사용)
        setStateWithPossibleStrictModeDoubleTrigger({
          update: (s) => s,
          commitToUndoStack: false,
          replaceCurrentUndoEntry: true,
        });
      } else if (commitToUndoStack) {
        setStateWithPossibleStrictModeDoubleTrigger({
          update: (s) => s,
          commitToUndoStack: true,
        });
      }
    },
    [pushHistory, setStateWithPossibleStrictModeDoubleTrigger],
  );

  const readContext = useMemo(
    (): TimelineContext => ({
      // trimming이 완료된 후에만 timeline을 다시 그립니다.
      durationInFrames: lastDurationWhileNotTrimming.current,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastDurationWhileNotTrimming.current],
  );

  const fpsContext = useMemo(
    (): FpsContext => ({
      fps: state.undoableState.fps,
    }),
    [state.undoableState.fps],
  );

  const dimensionsContext = useMemo(
    (): DimensionsContext => ({
      compositionWidth: state.undoableState.compositionWidth,
      compositionHeight: state.undoableState.compositionHeight,
    }),
    [
      state.undoableState.compositionWidth,
      state.undoableState.compositionHeight,
    ],
  );

  const selectedItemsContext = useMemo(
    (): SelectedItemsContext => ({
      selectedItems: state.selectedItems,
      activeGroupId: state.activeGroupId,
      inspectedGroupMemberId: state.inspectedGroupMemberId,
    }),
    [state.selectedItems, state.activeGroupId, state.inspectedGroupMemberId],
  );

  const assetsContext = useMemo(
    (): AssetsContext => ({
      assets: state.undoableState.assets,
    }),
    [state.undoableState.assets],
  );

  const assetStatusContext = useMemo(
    (): AssetStatusContext => ({
      assetStatus: state.assetStatus,
    }),
    [state.assetStatus],
  );

  const tracksContext = useMemo(
    (): TracksContext => ({
      tracks: state.undoableState.tracks,
    }),
    [state.undoableState.tracks],
  );

  const writeContext = useMemo(
    (): TimelineWriteOnlyContext => ({
      getCurrentState: () =>
        commandScopeAlive.current ? imperativeState.current : null,
      setState,
      undo,
      redo,
    }),
    [setState, undo, redo],
  );

  const canUseUndoStackContext = useMemo(
    (): CanUseUndoStackContext => ({
      canUndo,
      canRedo,
    }),
    [canUndo, canRedo],
  );

  const currentStateContext = useMemo(
    (): CurrentStateContext => ({
      state: imperativeState,
    }),
    [imperativeState],
  );

  const allItemsContext = useMemo(
    (): AllItemsContext => ({
      items: state.undoableState.items,
    }),
    [state.undoableState.items],
  );

  const transitionsContext = useMemo(
    (): TransitionsContext => ({
      transitions: state.undoableState.transitions,
    }),
    [state.undoableState.transitions],
  );

  const textItemHoverPreviewContext = useMemo(
    (): TextItemHoverPreview | null => state.textItemHoverPreview,
    [state.textItemHoverPreview],
  );

  const renderingContext = useMemo(
    (): RenderingTask[] => state.renderingTasks,
    [state.renderingTasks],
  );

  const captionStateContext = useMemo(
    (): CaptioningTask[] => state.captioningTasks,
    [state.captioningTasks],
  );

  const itemsBeingTrimmedContext = useMemo(
    (): ItemBeingTrimmed[] => state.itemsBeingTrimmed,
    [state.itemsBeingTrimmed],
  );

  const activeTimelineSnapContext = useMemo(
    (): ActiveTimelineSnap => ({
      activeSnapPoint: state.activeSnapPoint,
    }),
    [state.activeSnapPoint],
  );

  const backgroundColorContext = useMemo(
    (): BackgroundColorContext => ({
      backgroundColor: state.undoableState.backgroundColor,
    }),
    [state.undoableState.backgroundColor],
  );

  const brandTemplateIdContext = useMemo(
    (): BrandTemplateIdContext => ({
      brandTemplateId: state.brandTemplateId,
    }),
    [state.brandTemplateId],
  );

  const maskContext = useMemo(
    (): MaskContextValue => ({
      mask: state.undoableState.mask,
    }),
    [state.undoableState.mask],
  );

  // 이렇게 깊게 중첩된 context provider가 있는 이유:
  // https://remotion.dev/docs/editor-starter/state-management#contexts
  return (
    <TimelineContext.Provider value={readContext}>
      <TimelineWriteOnlyContext.Provider value={writeContext}>
        <FpsContext.Provider value={fpsContext}>
          <DimensionsContext.Provider value={dimensionsContext}>
            <BackgroundColorContext.Provider value={backgroundColorContext}>
              <MaskContext.Provider value={maskContext}>
                <BrandTemplateIdContext.Provider value={brandTemplateIdContext}>
                  <SelectedItemsContext.Provider value={selectedItemsContext}>
                    <AssetsContext.Provider value={assetsContext}>
                      <AssetStatusContext.Provider value={assetStatusContext}>
                        <TracksContext.Provider value={tracksContext}>
                          <FullStateContext.Provider value={state}>
                            <CanUseUndoStackContext.Provider
                              value={canUseUndoStackContext}
                            >
                              <CurrentStateContext.Provider
                                value={currentStateContext}
                              >
                                <AllItemsContext.Provider
                                  value={allItemsContext}
                                >
                                  <TransitionsContext.Provider
                                    value={transitionsContext}
                                  >
                                    <TextItemEditingContext.Provider
                                      value={state.textItemEditing}
                                    >
                                      <ItemSelectedForCropContext.Provider
                                        value={state.itemSelectedForCrop}
                                      >
                                        <ItemSelectedForZoomRegionContext.Provider
                                          value={
                                            state.itemSelectedForZoomRegion
                                          }
                                        >
                                          <TextItemHoverPreviewContext.Provider
                                            value={textItemHoverPreviewContext}
                                          >
                                            <RenderingContext.Provider
                                              value={renderingContext}
                                            >
                                              <CaptionStateContext.Provider
                                                value={captionStateContext}
                                              >
                                                <StateInitializedContext.Provider
                                                  value={state.initialized}
                                                >
                                                  <ItemsBeingTrimmedContext.Provider
                                                    value={
                                                      itemsBeingTrimmedContext
                                                    }
                                                  >
                                                    <LoopContext.Provider
                                                      value={state.loop}
                                                    >
                                                      <TimelineSnappingEnabledContext.Provider
                                                        value={
                                                          state.isSnappingEnabled
                                                        }
                                                      >
                                                        <SkimmingEnabledContext.Provider
                                                          value={
                                                            state.isSkimmingEnabled
                                                          }
                                                        >
                                                          <LinkingEnabledContext.Provider
                                                            value={
                                                              state.isLinkingEnabled
                                                            }
                                                          >
                                                            <TimelineCollapsedContext.Provider
                                                              value={
                                                                state.isTimelineCollapsed
                                                              }
                                                            >
                                                              <TimelineHeightContext.Provider
                                                                value={
                                                                  state.timelineHeight
                                                                }
                                                              >
                                                                <ActiveTimelineSnapContext.Provider
                                                                  value={
                                                                    activeTimelineSnapContext
                                                                  }
                                                                >
                                                                  <EditModeProvider>
                                                                    <SmartGuideProvider>
                                                                      <DragPreviewProvider>
                                                                        <AssetDropPreviewProvider>
                                                                          <TransitionDropPreviewProvider>
                                                                            <AnimationDropPreviewProvider>
                                                                              <TimelineZoomProvider>
                                                                                <TextSelectionProvider>
                                                                                  <AssetFramesProvider>
                                                                                    {
                                                                                      children
                                                                                    }
                                                                                  </AssetFramesProvider>
                                                                                </TextSelectionProvider>
                                                                              </TimelineZoomProvider>
                                                                            </AnimationDropPreviewProvider>
                                                                          </TransitionDropPreviewProvider>
                                                                        </AssetDropPreviewProvider>
                                                                      </DragPreviewProvider>
                                                                    </SmartGuideProvider>
                                                                  </EditModeProvider>
                                                                </ActiveTimelineSnapContext.Provider>
                                                              </TimelineHeightContext.Provider>
                                                            </TimelineCollapsedContext.Provider>
                                                          </LinkingEnabledContext.Provider>
                                                        </SkimmingEnabledContext.Provider>
                                                      </TimelineSnappingEnabledContext.Provider>
                                                    </LoopContext.Provider>
                                                  </ItemsBeingTrimmedContext.Provider>
                                                </StateInitializedContext.Provider>
                                              </CaptionStateContext.Provider>
                                            </RenderingContext.Provider>
                                          </TextItemHoverPreviewContext.Provider>
                                        </ItemSelectedForZoomRegionContext.Provider>
                                      </ItemSelectedForCropContext.Provider>
                                    </TextItemEditingContext.Provider>
                                  </TransitionsContext.Provider>
                                </AllItemsContext.Provider>
                              </CurrentStateContext.Provider>
                            </CanUseUndoStackContext.Provider>
                          </FullStateContext.Provider>
                        </TracksContext.Provider>
                      </AssetStatusContext.Provider>
                    </AssetsContext.Provider>
                  </SelectedItemsContext.Provider>
                </BrandTemplateIdContext.Provider>
              </MaskContext.Provider>
            </BackgroundColorContext.Provider>
          </DimensionsContext.Provider>
        </FpsContext.Provider>
      </TimelineWriteOnlyContext.Provider>
    </TimelineContext.Provider>
  );
};
